"use client";

import { useMemo, useState } from "react";
import { getAvatar } from "../helper";
import { ArrowUpDown } from "lucide-react";

export type DashboardOverviewProps = {
  studentsData: studentData[];
  isVisible: boolean;
};

export type studentData = {
  id: number;
  name: string;
  rollNo: string;
  class: number;
  section: "A" | "B" | "C" | "D" | "E";
  wordsLearned: number;
  englishCorrect: number;
  teluguCorrect: number;
  drawnCorrect: number;
  wordsSkipped: number;
  lastPractice: string;
};

const DashboardOverview = (props: {
  studentsData: DashboardOverviewProps["studentsData"];
  isVisible: boolean;
}) => {
  const { studentsData, isVisible } = props;

  type SortableColumn =
    | "name"
    | "rollNo"
    | "class"
    | "section"
    | "wordsSkipped"
    | "lastPractice";

  const [sortConfig, setSortConfig] = useState<{
    key: SortableColumn;
    direction: "asc" | "desc";
  } | null>(null);

  const handleSort = (key: SortableColumn) => {
    setSortConfig((current) => {
      if (!current || current.key !== key) {
        return { key, direction: "asc" };
      }

      return {
        key,
        direction: current.direction === "asc" ? "desc" : "asc",
      };
    });
  };

  const sortedStudents = useMemo(() => {
    if (!sortConfig) return studentsData;

    const sorted = [...studentsData];
    const directionMultiplier = sortConfig.direction === "asc" ? 1 : -1;

    const compare = (a: studentData, b: studentData) => {
      switch (sortConfig.key) {
        case "name":
          return a.name.localeCompare(b.name);
        case "rollNo": {
          const aNum = Number(a.rollNo);
          const bNum = Number(b.rollNo);
          if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
            return aNum - bNum;
          }
          return a.rollNo.localeCompare(b.rollNo);
        }
        case "class":
          return a.class - b.class;
        case "section":
          return a.section.localeCompare(b.section);
        case "wordsSkipped":
          return a.wordsSkipped - b.wordsSkipped;
        case "lastPractice": {
          const aTime = Date.parse(a.lastPractice);
          const bTime = Date.parse(b.lastPractice);
          if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
            return aTime - bTime;
          }
          return a.lastPractice.localeCompare(b.lastPractice);
        }
        default:
          return 0;
      }
    };

    sorted.sort((a, b) => compare(a, b) * directionMultiplier);
    return sorted;
  }, [studentsData, sortConfig]);

  const getSortStyle = (key: SortableColumn) => {
    const isActive = sortConfig?.key === key;
    return isActive
      ? "text-[#5f438a]"
      : "text-[#7b649f] hover:text-[#5f438a]";
  };

  return (
    <div
      className={`overflow-hidden rounded-2xl border border-[#d9caeb] bg-white/45 shadow-sm backdrop-blur-lg transition-all duration-700 ${
        isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
      }`}
    >
      <div className="p-3 md:p-4">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1080px] text-sm">
            <thead>
              <tr className="border-b border-[#dccdf0] text-[#7b649f]">
                <th className="px-3 py-3 text-left font-semibold">
                  <button
                    type="button"
                    onClick={() => handleSort("name")}
                    className={`inline-flex items-center gap-1 transition ${getSortStyle("name")}`}
                    aria-label="Sort by Name"
                  >
                    Name
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </th>
                <th className="px-3 py-3 text-center font-semibold">
                  <button
                    type="button"
                    onClick={() => handleSort("rollNo")}
                    className={`inline-flex items-center gap-1 transition ${getSortStyle("rollNo")}`}
                    aria-label="Sort by Roll Number"
                  >
                    Roll No
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </th>
                <th className="px-3 py-3 text-center font-semibold">
                  <button
                    type="button"
                    onClick={() => handleSort("class")}
                    className={`inline-flex items-center gap-1 transition ${getSortStyle("class")}`}
                    aria-label="Sort by Grade"
                  >
                    Grade
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </th>
                <th className="px-3 py-3 text-center font-semibold">
                  <button
                    type="button"
                    onClick={() => handleSort("section")}
                    className={`inline-flex items-center gap-1 transition ${getSortStyle("section")}`}
                    aria-label="Sort by Section"
                  >
                    Section
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </th>
                <th className="px-3 py-3 text-center font-semibold">Words learnt</th>
                <th className="px-3 py-3 text-center font-semibold">
                  Pronounced correctly (English)
                </th>
                <th className="px-3 py-3 text-center font-semibold">
                  Pronounced correctly (Telugu)
                </th>
                <th className="px-3 py-3 text-center font-semibold">Drawn correctly</th>
                <th className="px-3 py-3 text-center font-semibold">
                  <button
                    type="button"
                    onClick={() => handleSort("wordsSkipped")}
                    className={`inline-flex items-center gap-1 transition ${getSortStyle("wordsSkipped")}`}
                    aria-label="Sort by Words Skipped"
                  >
                    Words Skipped
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </th>
                <th className="px-3 py-3 text-center font-semibold">
                  <button
                    type="button"
                    onClick={() => handleSort("lastPractice")}
                    className={`inline-flex items-center gap-1 transition ${getSortStyle("lastPractice")}`}
                    aria-label="Sort by Last Practiced"
                  >
                    Last Practiced
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedStudents.map((student, index) => (
                <tr
                  key={student.id}
                  className="group border-b border-[#eadff8]/70 text-[#6b558f] transition hover:bg-white/40"
                  style={{
                    animationDelay: `${index * 200}ms`,
                    animationName: isVisible ? "slideInUp" : "none",
                    animationDuration: "0.6s",
                    animationTimingFunction: "ease-out",
                    animationFillMode: "forwards",
                  }}
                >
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white shadow ${getAvatar(
                          student.name
                        )}`}
                      >
                        😊
                      </div>
                      <span className="font-semibold text-[#57417d]">
                        {student.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center font-medium">
                    {student.rollNo}
                  </td>
                  <td className="px-3 py-3 text-center font-medium">
                    {student.class}
                  </td>
                  <td className="px-3 py-3 text-center font-medium">{student.section}</td>
                  <td className="px-3 py-3 text-center">
                    <span className="rounded-full bg-[#f0e4ff] px-3 py-1 font-semibold text-[#75549b]">
                      {student.wordsLearned}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="rounded-full bg-[#ece4ff] px-3 py-1 font-semibold text-[#6549a1]">
                      {student.englishCorrect}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="rounded-full bg-[#ece4ff] px-3 py-1 font-semibold text-[#6549a1]">
                      {student.teluguCorrect}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="rounded-full bg-[#ece4ff] px-3 py-1 font-semibold text-[#6549a1]">
                      {student.drawnCorrect}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="rounded-full bg-[#ece4ff] px-3 py-1 font-semibold text-[#6549a1]">
                      {student.wordsSkipped}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center text-xs font-medium md:text-sm">
                    <span className="rounded-lg bg-white/80 px-2 py-1 text-[#6d5b8b]">
                      {student.lastPractice}
                    </span>
                  </td>
                </tr>
              ))}

              {sortedStudents.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    className="px-3 py-8 text-center text-sm font-medium text-[#8f7bb4]"
                  >
                    No students found for selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
